import React, { useState, useEffect } from "react";
import axios from "axios";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Row, Col, Form, Button, InputGroup } from "react-bootstrap";
import Divider from "components/common/divider";
import { toast } from "react-toastify";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useAppContext } from "providers/appProvider";
import Select from "react-select";
import { getDomainConfig } from "../../../../config/domainConfig";
import {
  validateIBAN,
  validateVAT,
  validateKvK,
  validateGST,
  validateIFSC,
  validateAccountNumber,
  validateGAccount
} from "./../../../../helpers/financeValidation";

const NewLocation = ({ onSuccess, onCancel }) => {
  const { t, i18n } = useTranslation();
  const domainConfig = getDomainConfig();
  const finLabels = domainConfig.financialLabels;
  const countryOptions = domainConfig.countries.map(c => ({
    value: c.value,
    label: t(c.label),
  }));
  const { setLocations } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);

  // ── Schema defined inline, same pattern as NewProposal ─────────────────
  const schema = yup.object({
    name:             yup.string().required(t("locationNameRequired")),
    streetName:       yup.string().required(t("fieldRequiredText")),
    houseNumber:      yup.string().required(t("fieldRequiredText")),
    addition:         yup.string().nullable(),
    postCode:         yup.string().required(t("fieldRequiredText")),
    city:             yup.string().required(t("fieldRequiredText")),
    country:          yup.string().required(t("fieldRequiredText")),

    // NL fields
    iban: finLabels.showIfsc
      ? yup.string().nullable()
      : yup.string().nullable().test("iban", t("ibanInvalidFormat"), validateIBAN),

    btwDetails: finLabels.showIfsc
      ? yup.string().nullable()
      : yup.string().nullable().test("vat", t("vatInvalidFormat"), validateVAT),

    kvkDetails: finLabels.showKvk
      ? yup.string().nullable().test("kvk", t("cocInvalidFormat"), validateKvK)
      : yup.string().nullable(),

    gAccount: finLabels.showGAccount
      ? yup
        .string()
        .nullable()
        .test(
          "gAccount",
          t("gAccountInvalidFormat"),
          validateGAccount
        )
      : yup.string().nullable(),

    // IN fields
    gstDetails: finLabels.showIfsc
      ? yup.string().nullable().test("gst", t("gstInvalidFormat"), validateGST)
      : yup.string().nullable(),

    accountNumber: finLabels.showAccount
      ? yup.string().nullable().test("account", t("accountInvalidFormat"), validateAccountNumber)
      : yup.string().nullable(),

    ifscCode: finLabels.showIfsc
      ? yup.string().nullable().test("ifsc", t("ifscInvalidFormat"), validateIFSC)
      : yup.string().nullable(),

    asperionUserId:   yup.string().required(t("userIdRequired")),
    asperionPassword: yup.string().required(t("passwordRequired")),
    asperionTenantId: yup.string().required(t("tenantIdRequired")),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    register,
    reset,
    trigger,
  } = useForm({
    mode: "onChange",   
    resolver: yupResolver(schema),
    defaultValues: {
      country: domainConfig.countries[0]?.value || "",
    },
  });

  const onSubmit = async (data) => {
    setIsLoading(true);
    setError("");

    const transformedData = Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key,
        value == null || String(value).trim() === "" ? null : value,
      ])
    );

    const currentCustomerId = localStorage.getItem("customerId");
    if (!currentCustomerId) {
      setIsLoading(false);
      setError("CustomerId is missing.");
      return;
    }

    transformedData.customerId = currentCustomerId;

    if (transformedData.asperionPassword) {
      transformedData.asperionPassword = btoa(transformedData.asperionPassword);
    }

    transformedData.autoAsperionSyncFlag =
      process.env.REACT_APP_ORGANIZATION_NAME === "GetPlan" ? false : true;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/locationRoute/new`,
        transformedData,
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );
      setLocations(prev => [...prev, response.data]);
      reset(); // Reset the form
      toast.success(t("branchAddedSuccessMessage"), {
        position: "top-right",
        autoClose: 5000,
      });
      onSuccess();
    } catch (err) {
      setError(t("branchAddErrorMessage"));
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDiscard = () => {
    reset();
    onCancel();
  };

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      trigger(Object.keys(errors));
    }
  }, [i18n.language]);

  return (
    <div className="form-container">
      <Form onSubmit={handleSubmit(onSubmit)} className="data-font-colour">
        <h5 className="bg-body-tertiary p-3">{t("newBranch")}</h5>
        {/* Name Section */}
        <Row className="px-3">
          <Col md="2" className="mb-2">
            <Form.Label className="data-font-size">
              {t("name")}<span className="text-danger">*</span>
            </Form.Label>
          </Col>
          <Col md="4" className="mb-2">
            <Form.Control
              type="text"
              {...register("name", { required: t("branchNameRequired") })}
              isInvalid={!!errors.name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.name?.message}
            </Form.Control.Feedback>
          </Col>
        </Row>

        {/* Address */}
        <h5 className="bg-body-tertiary p-3">{t("address")}</h5>
        <Row className="px-3">
          <Col md="2" className="mb-2">
            <Form.Label className="data-font-size">
              {t("streetName")}<span className="text-danger">*</span>
            </Form.Label>
          </Col>
          <Col md="4" className="mb-2">
            <Form.Control
              type="text"
              {...register("streetName")}
              isInvalid={!!errors.streetName}
            />
            <Form.Control.Feedback type="invalid">
              {errors.streetName?.message}
            </Form.Control.Feedback>
          </Col>

          <Col md="2" className="mb-2">
            <Form.Label className="data-font-size">
              {t("houseNumber")}<span className="text-danger">*</span>
            </Form.Label>
          </Col>
          <Col md="4" className="mb-2">
            <Form.Control
              type="text"
              {...register("houseNumber")}
              isInvalid={!!errors.houseNumber}
            />
            <Form.Control.Feedback type="invalid">
              {errors.houseNumber?.message}
            </Form.Control.Feedback>
          </Col>

          <Col md="2" className="mb-2">
            <Form.Label className="data-font-size">{t("addition")}</Form.Label>
          </Col>
          <Col md="4" className="mb-2">
            <Form.Control type="text" {...register("addition")} />
          </Col>

          <Col md="2" className="mb-2">
            <Form.Label className="data-font-size">
              {t("postcode")}<span className="text-danger">*</span>
            </Form.Label>
          </Col>
          <Col md="4" className="mb-2">
            <Form.Control
              type="text"
              {...register("postCode")}
              isInvalid={!!errors.postCode}
            />
            <Form.Control.Feedback type="invalid">
              {errors.postCode?.message}
            </Form.Control.Feedback>
          </Col>

          <Col md="2" className="mb-2">
            <Form.Label className="data-font-size">
              {t("city")}<span className="text-danger">*</span>
            </Form.Label>
          </Col>
          <Col md="4" className="mb-2">
            <Form.Control
              type="text"
              {...register("city")}
              isInvalid={!!errors.city}
            />
            <Form.Control.Feedback type="invalid">
              {errors.city?.message}
            </Form.Control.Feedback>
          </Col>

          <Col md="2" className="mb-2">
            <Form.Label className="data-font-size">
              {t("country")}<span className="text-danger">*</span>
            </Form.Label>
          </Col>
          <Col md="4" className="mb-2">
            <Controller
              name="country"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Select
                    key={i18n.language}
                    {...field}
                    options={countryOptions}
                    isClearable
                    value={countryOptions.find(opt => opt.value === field.value) || null}
                    onChange={(selected) => field.onChange(selected ? selected.value : "")}
                    className={`custom-select-container ${error ? "is-invalid" : ""}`}
                    classNamePrefix="custom-select"
                    placeholder={t("select")}
                    noOptionsMessage={() => t("noOptionsAvailable")}
                  />
                  {error && (
                    <div className="invalid-feedback d-block">{error.message}</div>
                  )}
                </>
              )}
            />
          </Col>
        </Row>

        {/* Financial Details */}
        <h5 className="bg-body-tertiary p-3">{t("financialDetails")}</h5>
        <Row className="px-3">

          {/* IBAN — NL only */}
          {!finLabels.showIfsc && (
            <>
              <Col md="2" className="mb-2">
                <Form.Label className="data-font-size">{t("iban")}</Form.Label>
              </Col>
              <Col md="4" className="mb-2">
                <Form.Control
                  type="text"
                  placeholder={finLabels.ibanPlaceholder}
                  {...register("iban")}
                  isInvalid={!!errors.iban}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.iban?.message}
                </Form.Control.Feedback>
              </Col>
            </>
          )}

          {/* VAT / BTW — NL only */}
          {!finLabels.showIfsc && (
            <>
              <Col md="2" className="mb-2">
                <Form.Label className="data-font-size">{t(finLabels.vat)}</Form.Label>
              </Col>
              <Col md="4" className="mb-2">
                <Form.Control
                  type="text"
                  placeholder={finLabels.vatPlaceholder}
                  {...register("btwDetails")}
                  isInvalid={!!errors.btwDetails}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.btwDetails?.message}
                </Form.Control.Feedback>
              </Col>
            </>
          )}

          {/* KvK — NL only */}
          {finLabels.showKvk && (
            <>
              <Col md="2" className="mb-2">
                <Form.Label className="data-font-size">{t("kvkDetails")}</Form.Label>
              </Col>
              <Col md="4" className="mb-2">
                <Form.Control
                  type="text"
                  placeholder={finLabels.cocNumberPlaceholder}
                  {...register("kvkDetails")}
                  isInvalid={!!errors.kvkDetails}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.kvkDetails?.message}
                </Form.Control.Feedback>
              </Col>
            </>
          )}

          {/* GAccount — NL only */}
          {finLabels.showGAccount && (
            <>
              <Col md="2" className="mb-2">
                <Form.Label className="data-font-size">{t("gAccount")}</Form.Label>
              </Col>
              <Col md="4" className="mb-2">
                <Form.Control
                  type="text"
                  placeholder={finLabels.gAccountPlaceholder}
                  {...register("gAccount")}
                  isInvalid={!!errors.gAccount}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.gAccount?.message}
                </Form.Control.Feedback>
              </Col>
            </>
          )}

          {/* Account Number — IN only */}
          {finLabels.showAccount && (
            <>
              <Col md="2" className="mb-2">
                <Form.Label className="data-font-size">{t(finLabels.account)}</Form.Label>
              </Col>
              <Col md="4" className="mb-2">
                <Form.Control
                  type="text"
                  placeholder={finLabels.accountPlaceholder}
                  {...register("accountNumber")}
                  isInvalid={!!errors.accountNumber}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.accountNumber?.message}
                </Form.Control.Feedback>
              </Col>
            </>
          )}

          {/* IFSC — IN only */}
          {finLabels.showIfsc && (
            <>
              <Col md="2" className="mb-2">
                <Form.Label className="data-font-size">{t(finLabels.ifsc)}</Form.Label>
              </Col>
              <Col md="4" className="mb-2">
                <Form.Control
                  type="text"
                  placeholder={finLabels.ifscPlaceholder}
                  {...register("ifscCode")}
                  isInvalid={!!errors.ifscCode}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.ifscCode?.message}
                </Form.Control.Feedback>
              </Col>
            </>
          )}

           {/* GST — IN only */}
          {finLabels.showIfsc && (
            <>
              <Col md="2" className="mb-2">
                <Form.Label className="data-font-size">{t(finLabels.gst)}</Form.Label>
              </Col>
              <Col md="4" className="mb-2">
                <Form.Control
                  type="text"
                  placeholder={finLabels.gstPlaceholder}
                  {...register("gstDetails")}
                  isInvalid={!!errors.gstDetails}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.gstDetails?.message}
                </Form.Control.Feedback>
              </Col>
            </>
          )}
        </Row>

        {/* Asperion Details */}
        <h5 className="bg-body-tertiary p-3">{t("asperionDetails")}</h5>
        <Row className="px-3">
          <Col md="2" className="mb-2">
            <Form.Label className="data-font-size">
              {t("userId")}<span className="text-danger">*</span>
            </Form.Label>
          </Col>
          <Col md="4" className="mb-2">
            <Form.Control
              type="text"
              {...register("asperionUserId")}
              isInvalid={!!errors.asperionUserId}
            />
            <Form.Control.Feedback type="invalid">
              {errors.asperionUserId?.message}
            </Form.Control.Feedback>
          </Col>

          <Col md="2" className="mb-2">
            <Form.Label className="data-font-size">
              {t("password")}<span className="text-danger">*</span>
            </Form.Label>
          </Col>
          <Col md="4" className="mb-2">
            <InputGroup>
              <Form.Control
                type={passwordVisible ? "text" : "password"}
                {...register("asperionPassword")}
                isInvalid={!!errors.asperionPassword}
              />
              <InputGroup.Text onClick={() => setPasswordVisible(!passwordVisible)}>
                {passwordVisible ? <FaEyeSlash /> : <FaEye />}
              </InputGroup.Text>
            </InputGroup>
            {errors.asperionPassword && (
              <Form.Control.Feedback type="invalid" className="d-block">
                {errors.asperionPassword?.message}
              </Form.Control.Feedback>
            )}
          </Col>
        </Row>

        <Row className="px-3">
          <Col md="2" className="mb-2">
            <Form.Label className="data-font-size">
              {t("administrationNumber")}<span className="text-danger">*</span>
            </Form.Label>
          </Col>
          <Col md="4" className="mb-2">
            <Form.Control
              type="text"
              {...register("asperionTenantId")}
              isInvalid={!!errors.asperionTenantId}
            />
            <Form.Control.Feedback type="invalid">
              {errors.asperionTenantId?.message}
            </Form.Control.Feedback>
          </Col>
        </Row>

        <Divider />
        <Row className="justify-content-end">
          <Col xs="auto">
            <Button
              variant="falcon-default"
              size="sm"
              className="me-3"
              type="button"
              onClick={handleDiscard}
            >
              {t("cancel")}
            </Button>
            <Button variant="primary" size="sm" type="submit" disabled={isLoading}>
              {isLoading ? t("saving") : t("save")}
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default NewLocation;